import CM from '@oppobrowser/lib-browser-componentManager';
import wv from '@cpc/lib-browser-jsapi';
import { isBrowserVersionHigherThan } from './utils';
import { $, query } from '@js/utils';

import { BrowserBridgeHandler } from '../js/browserBridgeHandler';
import {
  browserPartner,
  channel,
  isListInfinity,
  isPushInfinity,
  isMixedVideo,
  useRelatedSearch,
  showReSearch,
  showRecommended,
  isSmallRedBook,
} from './constants';
import { reportReadingProgress, getSubjectActivityInfo } from './auxiliary';
import {
  feednewsComponentMap,
  feedsComponentMap,
  thirdComponentMap,
  simpleComponentMap,
  smallRedBookComponentMap,
} from '../components';
import {
  getBizTraceInfo,
  getClientCacheBizTrace,
  bizTraceCacheGet,
  bizTraceCacheSet,
} from '@js/native';
import { statH5 } from './statH5';
import createStore from './store';
import '../scss/index.scss';
import '../scss/article.scss';
import '../scss/banner.scss';
import 'swiper/css';
import 'swiper/css/pagination';

// 线上环境cdn域名路径
if (
  window.pageData.COMMON.ENV !== 'development' &&
  window.pageData.COMMON.CDN_HOST
) {
  __webpack_public_path__ = `//${window.pageData.COMMON.CDN_HOST}/`;
}

// 全局变量
window.wv = wv;

let eventContext = {};

const cm = (window.cm = new CM(eventContext));

let isDev = process.env.NODE_ENV === 'development';
let store;

// 暗色模式监听
initNightmodechange();

async function init() {
  // 模块控制
  window.modulesConfig =
    window.getModulesConfig?.({ isGovArticle: false }, 'detail', 0) || {};
  store = createStore();
  // 把store挂载到window上，方便其他组件使用
  window.store = store;
  // 存储归因参数
  let bizTraceInfo;
  if (bizTraceCacheGet(query.get('__docId__'))?.value?.transparent) {
    bizTraceInfo = bizTraceCacheGet(query.get('__docId__')).value;
  } else if (getBizTraceInfo?.().transparent) {
    bizTraceInfo = getBizTraceInfo();
  } else if (getClientCacheBizTrace(query.get('__docId__')).transparent) {
    bizTraceInfo = getClientCacheBizTrace(query.get('__docId__'));
  }
  bizTraceCacheSet(query.get('__docId__'), bizTraceInfo);

  // 上报客户端做数据统计
  getSubjectActivityInfo();
  // 页面组件注册
  await componentManagerRun();
  // 阅读进度监听
  reportReadingProgress();
  // ready(domReadyHandler);

  // 处理视频
  let videoEls = document.querySelectorAll('.x5-video-player');
  for (let i = 0; i < videoEls.length; i++) {
    videoEls[i].addEventListener('touchmove', function (event) {
      event.preventDefault();
    });
  }
}

// feeds-端外信息流无需jsapi授权；非feeds-端内打开需要做jsapi授权处理
console.log('process.env.OFET_PAGE_TYPE', process.env.OFET_PAGE_TYPE);
if (process.env.OFET_PAGE_TYPE !== 'feeds') {
  wv.config({
    ...window.pageData.browserSecret,
  }).ready(init);
} else {
  init();
}

// 来源非浏览器主端（如：feeds页面）使用h5上报数据
if (browserPartner !== 'browser') {
  statH5.statPageLife();
  Object.assign(wv, statH5);
}

// 夜间模式监听
function initNightmodechange() {
  if (window.isNightMode) {
    let _isNightMode = window.isNightMode();
    document.documentElement.dataset.theme = _isNightMode ? 'dark' : 'day';
    window.addEventListener('nightmodechange', () => {
      _isNightMode = window.isNightMode();
      document.documentElement.dataset.theme = _isNightMode ? 'dark' : 'day';
    });
  }

  // 适配通用信息流
  // if (window.OppoWebPage && window.OppoWebPage.isNightMode){
  //   let _isNightMode = window.OppoWebPage.isNightMode();
  //   document.documentElement.dataset.theme = _isNightMode ? 'dark' : 'day';
  // }
}

// 端内详情页需要的子组件
function getFeednewsComponents() {
  if (isSmallRedBook) {
    return Object.values(smallRedBookComponentMap);
  }

  if (store.pageType === 'gallery') return [];
  if (store.pageMode === 'simple') {
    return componentsFilter(simpleComponentMap);
  }
  // 自有详情，淘宝源，不展示meta
  if (['taobao'].indexOf(store.source) > -1) delete feednewsComponentMap.meta;

  // 自建源根据客户端api确定是否删除相关搜索组件
  if (
    window.wv.isMethodValid('BrowserIFlow.isBrowserHome') &&
    window.wv.invoke('BrowserIFlow.isBrowserHome')?.data === false
  ) {
    delete feednewsComponentMap.relatedSearch;
  }
  return componentsFilter(feednewsComponentMap);
}

// 三方源需要的子组件（tencent、sina、zhihu）
async function getThirdComponents() {
  if (store.pageMode === 'simple') {
    return componentsFilter(simpleComponentMap);
  }
  // 三方源（tencent、sina）根据客户端api确定是否删除相关搜索组件
  if (
    window.wv.isMethodValid('BrowserIFlow.isBrowserHome') &&
    window.wv.invoke('BrowserIFlow.isBrowserHome')?.data === false
  ) {
    delete thirdComponentMap.relatedSearch;
  }
  return componentsFilter(thirdComponentMap);
}

// 端外信息流需要的子组件（针对feeds）
async function getFeedsComponents() {
  if (store.pageMode === 'simple') {
    return componentsFilter(simpleComponentMap);
  }
  // 非视频类型去除视频组件
  if (store.pageType !== 'video') delete feedsComponentMap.videoPlayer;
  return Object.values(feedsComponentMap);
}

/**
 * 隐藏评论组件
 * 因为评论组件里面有调用方法传数据给客户端
 */
function hideComment() {
  window.globalTheme?.appendCssToPage(
    '#feeds_comment{display: none;}',
    'globalTheme-hide-feeds_comment',
  );
}

// 子组件过滤处理（不同业务场景）
async function componentsFilter(componentsMap) {
  const articleFoldConfig = BrowserBridgeHandler.getArticleFoldConfig();
  let components = [];
  const { pageType } = store;

  for (let key in componentsMap) {
    if (
      Object.keys(window.modulesConfig || {}).includes(key) &&
      window.modulesConfig[key]?.isShow === false
    ) {
      if (key === 'comment') {
        hideComment();
      } else if (articleFoldConfig?.switch?.oppo && key === 'showMore') {
        // 全文折叠独立配置开关开启了就默认不注销全文折叠组件
        continue;
      } else {
        delete componentsMap[key];
      }
    }
  }

  // 非视频类型去除视频组件
  if (pageType !== 'video') delete componentsMap.videoPlayer;

  if (componentsMap.hotSearch) {
    components = filterPushHotSearch(
      componentsMap,
      window.modulesConfig?.hotSearch?.position,
    );
  }

  components = Object.values(componentsMap);

  return components;
}

/**详情页热榜展示位置调整
 *   1: 'after-ad',  // 在第一个广告位下面
 *   2: 'before-ad'  // 在第一个广告位上面
 * 热榜展示在第一个广告下方：1；热榜展示在第一个广告上方：2；
 */
function filterPushHotSearch(componentsMap, position) {
  let hotSearchPosition = position === 'after-ad' ? 1 : 2;

  let keys = Object.keys(componentsMap);
  var map = new Map();
  for (let i = 0; i < keys.length; i++) {
    if (keys[i].includes('ad_afterArticle')) {
      if (hotSearchPosition == 1) {
        keys.splice(i + 1, 0, 'hotSearch');
      } else if (hotSearchPosition == 2) {
        keys.splice(i, 0, 'hotSearch');
        map.set(keys[i], componentsMap.hotSearch);
        ++i;
      }
    }
    map.set(keys[i], componentsMap[keys[i]]);
  }
  return Array.from(map.values());
  // }
}

// 区分信息流场景（端内、端外、三方）
async function getComponents() {
  let components = [];
  const pageType = process.env.OFET_PAGE_TYPE;
  switch (pageType) {
    // 三方源组件
    case 'feednewsThird':
      components = await getThirdComponents();
      break;
    // 端外组件
    case 'feeds':
      components = await getFeedsComponents();
      break;
    default:
      components = await getFeednewsComponents();
  }
  console.log('pageType类型：', pageType);
  console.log('组件列表：', components);

  return components;
}

/**
 * 根据页面内容，插入模板容器
 * @param components
 */
function initHtmlTemplate(components) {
  const content = getComponentFragement(components, 'feeds');
  const container = $('#feed-news-inject');
  container?.appendChild(content);
  const main = getComponentFragement(components, 'body');
  const body = $('body');
  body?.appendChild(main);
  const showMore = getComponentFragement(components, 'showMore');
  const showMoreBox = $('#showMore');
  if (showMoreBox) {
    showMoreBox.appendChild(showMore);
  }
}

function getComponentFragement(components, type) {
  const template = components
    .filter((com) => com.type === type)
    .map((com) => com.template)
    .join('');
  return document.createRange().createContextualFragment(template);
}

async function componentManagerRun() {
  const components = await getComponents();
  initHtmlTemplate(components);
  components.forEach((com) => {
    if (!com.component) {
      return;
    }
    const comKeys = Object.keys(cm.components);
    // 防止多次注册后，组件的事件也被多次注册，事件响应也会多次
    if (!comKeys.includes(com.name)) {
      cm.registe(com);
    }
  });
  cm.run();
}
